import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TimeOfDayPicker.module.css';

export type TimeOfDay = 'morning' | 'day' | 'evening' | 'night';

export type TimeValue =
  | { kind: 'exact'; hhmm: string; timeOfDay: TimeOfDay }
  | { kind: 'unknown'; timeOfDay: TimeOfDay };

interface TimeOfDayPickerProps {
  value: TimeOfDay | null;
  exactTime?: string | null;
  unknownMode?: boolean;
  onChange: (value: TimeOfDay, midpointHHmm: string) => void;
  onExactTimeChange?: (hhmm: string, timeOfDay: TimeOfDay) => void;
  onUnknownToggle?: (unknown: boolean) => void;
}

const MIDPOINTS: Record<TimeOfDay, string> = {
  morning: '09:00',
  day: '15:00',
  evening: '21:00',
  night: '03:00',
};

const ICONS: Record<TimeOfDay, string> = {
  morning: '☀',
  day: '◐',
  evening: '☾',
  night: '✦',
};

const RANGES: Record<TimeOfDay, string> = {
  morning: '6 – 12',
  day: '12 – 18',
  evening: '18 – 00',
  night: '00 – 6',
};

const OPTIONS: TimeOfDay[] = ['morning', 'day', 'evening', 'night'];

function deriveTimeOfDay(hhmm: string): TimeOfDay | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  if (Number.isNaN(h) || h < 0 || h > 23) return null;
  if (h >= 6 && h < 12) return 'morning';
  if (h >= 12 && h < 18) return 'day';
  if (h >= 18 && h < 24) return 'evening';
  return 'night';
}

export function TimeOfDayPicker({
  value,
  exactTime,
  unknownMode: unknownModeProp,
  onChange,
  onExactTimeChange,
  onUnknownToggle,
}: TimeOfDayPickerProps): JSX.Element {
  const { t } = useTranslation('flows');
  const [internalUnknown, setInternalUnknown] = useState<boolean>(
    unknownModeProp ?? false,
  );
  const [hh, setHh] = useState<string>(() => exactTime?.split(':')[0] ?? '');
  const [mm, setMm] = useState<string>(() => exactTime?.split(':')[1] ?? '');
  const hhRef = useRef<HTMLInputElement>(null);
  const mmRef = useRef<HTMLInputElement>(null);

  const isControlled = typeof unknownModeProp === 'boolean';
  const unknownMode = isControlled ? unknownModeProp : internalUnknown;

  useEffect(() => {
    if (exactTime) {
      const [h, m] = exactTime.split(':');
      if (h !== undefined) setHh(h);
      if (m !== undefined) setMm(m);
    }
  }, [exactTime]);

  function setUnknownMode(next: boolean): void {
    if (!isControlled) setInternalUnknown(next);
    onUnknownToggle?.(next);
  }

  function commitExact(nextHh: string, nextMm: string): void {
    const padded = `${nextHh.padStart(2, '0')}:${nextMm.padStart(2, '0')}`;
    const tod = deriveTimeOfDay(padded);
    if (tod) {
      onChange(tod, padded);
      onExactTimeChange?.(padded, tod);
    }
  }

  function handleHhChange(raw: string): void {
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    setHh(digits);
    if (digits.length === 2) {
      const n = parseInt(digits, 10);
      if (n >= 0 && n <= 23) {
        mmRef.current?.focus();
        if (mm.length === 2) commitExact(digits, mm);
      }
    }
  }

  function handleMmChange(raw: string): void {
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    setMm(digits);
    if (digits.length === 2 && hh.length >= 1) {
      const n = parseInt(digits, 10);
      if (n >= 0 && n <= 59) commitExact(hh, digits);
    }
  }

  function handleMmBlur(): void {
    if (hh.length >= 1 && mm.length >= 1) {
      commitExact(hh, mm);
    }
  }

  function toggleUnknown(): void {
    const next = !unknownMode;
    setUnknownMode(next);
    if (next) {
      // entering unknown mode — clear exact time
      setHh('');
      setMm('');
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.exactRow}>
        <div
          className={`${styles.hhmmBox} ${unknownMode ? styles.hhmmBoxMuted : ''}`}
          onClick={() => {
            if (unknownMode) return;
            hhRef.current?.focus();
          }}
          role="presentation"
        >
          <span className={styles.clockIcon} aria-hidden="true">⏱</span>
          <input
            ref={hhRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={2}
            value={hh}
            placeholder="HH"
            disabled={unknownMode}
            onChange={(e) => handleHhChange(e.target.value)}
            className={styles.timeInput}
            aria-label="hours"
          />
          <span className={styles.timeSep} aria-hidden="true">:</span>
          <input
            ref={mmRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={2}
            value={mm}
            placeholder="MM"
            disabled={unknownMode}
            onChange={(e) => handleMmChange(e.target.value)}
            onBlur={handleMmBlur}
            className={styles.timeInput}
            aria-label="minutes"
          />
        </div>
        <button
          type="button"
          className={`${styles.unknownBtn} ${unknownMode ? styles.unknownBtnActive : ''}`}
          onClick={toggleUnknown}
          aria-pressed={unknownMode}
        >
          {t('onboarding.birthData.timeUnknown')}
        </button>
      </div>

      {unknownMode && (
        <div
          className={styles.grid}
          role="radiogroup"
          aria-label={t('onboarding.birthData.birthTime')}
        >
          {OPTIONS.map((opt) => {
            const active = value === opt;
            return (
              <button
                key={opt}
                type="button"
                role="radio"
                aria-pressed={active}
                aria-checked={active}
                className={`${styles.btn} ${active ? styles.active : ''}`}
                onClick={() => onChange(opt, MIDPOINTS[opt])}
              >
                <span className={styles.icon} aria-hidden="true">{ICONS[opt]}</span>
                <span className={styles.label}>
                  {t(`onboarding.birthData.timeOfDay.${opt}`)}
                </span>
                <span className={styles.range} aria-hidden="true">{RANGES[opt]}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
